import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const AUTH_STORAGE_KEY = 'vent_remember_me';
const SUPABASE_AUTH_KEY = 'sb-qzxediukijlblhyerxjw-auth-token';
const REMEMBERED_EMAIL_KEY = 'vent_remembered_email';

/**
 * Check if running in native mobile app
 */
export const isNativeApp = (): boolean => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

/**
 * Get the user's "Remember Me" preference
 * On mobile: Always returns true (persistent sessions)
 * On web: Returns the stored preference
 */
export const getRememberMePreference = (): boolean => {
  if (isNativeApp()) {
    return true; // Mobile always remembers
  }
  return localStorage.getItem(AUTH_STORAGE_KEY) === 'true';
};

/**
 * Set the "Remember Me" preference before login
 * This must be called BEFORE signIn to ensure proper storage routing
 */
export const setRememberMePreference = (remember: boolean): void => {
  if (isNativeApp()) {
    // Mobile always uses localStorage, no need to set preference
    return;
  }
  
  if (remember) {
    localStorage.setItem(AUTH_STORAGE_KEY, 'true');
  } else {
    localStorage.setItem(AUTH_STORAGE_KEY, 'false');
    // Clear any existing localStorage session data when switching to non-remember
    localStorage.removeItem(SUPABASE_AUTH_KEY);
  }
};

/**
 * Clear the "Remember Me" preference on logout
 */
export const clearRememberMePreference = (): void => {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  // Also clear session data from both storages
  localStorage.removeItem(SUPABASE_AUTH_KEY);
  sessionStorage.removeItem(SUPABASE_AUTH_KEY);

  // Native: also clear Capacitor Preferences (fire-and-forget)
  if (isNativeApp()) {
    Preferences.remove({ key: AUTH_STORAGE_KEY }).catch(() => {});
    Preferences.remove({ key: SUPABASE_AUTH_KEY }).catch(() => {});
    Preferences.remove({ key: REMEMBERED_EMAIL_KEY }).catch(() => {});
  }
};

/**
 * Remembered email helpers (for prefilling sign-in on native/web).
 * Note: we never store passwords.
 */
export const getRememberedEmail = async (): Promise<string | null> => {
  if (isNativeApp()) {
    const { value } = await Preferences.get({ key: REMEMBERED_EMAIL_KEY });
    return value ?? null;
  }
  return localStorage.getItem(REMEMBERED_EMAIL_KEY);
};

export const setRememberedEmail = async (email: string | null): Promise<void> => {
  if (isNativeApp()) {
    if (!email) {
      await Preferences.remove({ key: REMEMBERED_EMAIL_KEY });
      return;
    }
    await Preferences.set({ key: REMEMBERED_EMAIL_KEY, value: email });
    return;
  }

  if (!email) {
    localStorage.removeItem(REMEMBERED_EMAIL_KEY);
    return;
  }
  localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
};

/**
 * Custom storage adapter that routes to the appropriate storage
 * based on platform and "Remember Me" preference
 */
export const authStorageAdapter = {
  getItem: async (key: string): Promise<string | null> => {
    // Native: use Capacitor Preferences for better persistence across app restarts
    if (isNativeApp()) {
      try {
        const { value } = await Preferences.get({ key });
        return value ?? null;
      } catch (e) {
        console.warn('[authStorage] Preferences.get failed, falling back to localStorage:', e);
        return localStorage.getItem(key);
      }
    }
    
    // Web: Check preference
    const rememberMe = getRememberMePreference();
    
    if (rememberMe) {
      // Check localStorage first, then sessionStorage for migration
      const localValue = localStorage.getItem(key);
      if (localValue) return localValue;
      return sessionStorage.getItem(key);
    } else {
      // Check sessionStorage first, then localStorage for migration
      const sessionValue = sessionStorage.getItem(key);
      if (sessionValue) return sessionValue;
      return localStorage.getItem(key);
    }
  },
  
  setItem: async (key: string, value: string): Promise<void> => {
    // Native: persist to Preferences
    if (isNativeApp()) {
      try {
        await Preferences.set({ key, value });
        return;
      } catch (e) {
        console.warn('[authStorage] Preferences.set failed, falling back to localStorage:', e);
        localStorage.setItem(key, value);
        return;
      }
    }
    
    // Web: Route based on preference
    const rememberMe = getRememberMePreference();
    
    if (rememberMe) {
      localStorage.setItem(key, value);
      sessionStorage.removeItem(key); // Clean up other storage
    } else {
      sessionStorage.setItem(key, value);
      localStorage.removeItem(key); // Clean up other storage
    }
  },
  
  removeItem: async (key: string): Promise<void> => {
    // Native: remove from Preferences
    if (isNativeApp()) {
      try {
        await Preferences.remove({ key });
        return;
      } catch (e) {
        console.warn('[authStorage] Preferences.remove failed, falling back to localStorage:', e);
      }
    }

    // Web: Remove from both storages to be safe
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  },
};
